import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Mail, Phone, Send, Check, Edit, Trash2, Share2, Heart, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";
import { SellerRatingSummary, ReviewDialog, ReviewList } from "@/components/SellerRating";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const reportReasons = [
  { value: "inappropriate", label: "Upassende innhold" },
  { value: "spam", label: "Spam eller falsk annonse" },
  { value: "fraud", label: "Mulig svindel" },
  { value: "wrong_category", label: "Feil kategori" },
  { value: "other", label: "Annet" },
];

const ListingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const [listing, setListing] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [similarListings, setSimilarListings] = useState([]);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportForm, setReportForm] = useState({ reason: "", description: "" });
  const [submittingReport, setSubmittingReport] = useState(false);
  const [messageForm, setMessageForm] = useState({
    sender_name: "",
    sender_email: "",
    message: "",
  });

  useEffect(() => {
    fetchListing();
    fetchMessages();
    checkIfFavorite();
    incrementView();
  }, [id]);

  useEffect(() => {
    if (listing) {
      fetchSimilarListings();
    }
  }, [listing]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      setListing(response.data);
      
      // Check if current user can edit this listing
      const isCreator = user && response.data.creator_id === user.id;
      setCanEdit(isCreator);
    } catch (error) {
      console.error("Error fetching listing:", error);
      toast.error("Kunne ikke laste inn annonse");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/${id}`);
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const incrementView = async () => {
    try {
      await axios.post(`${API}/listings/${id}/view`);
    } catch (error) {
      console.error("Error incrementing view:", error);
    }
  };

  const fetchSimilarListings = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}/similar?limit=3`);
      setSimilarListings(response.data);
    } catch (error) {
      console.error("Error fetching similar listings:", error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageForm.sender_name || !messageForm.sender_email || !messageForm.message) {
      toast.error("Vennligst fyll ut alle felt");
      return;
    }

    try {
      setSendingMessage(true);
      await axios.post(`${API}/messages`, {
        listing_id: id,
        ...messageForm,
      });
      toast.success("Melding sendt!");
      setMessageForm({ sender_name: "", sender_email: "", message: "" });
      fetchMessages();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Kunne ikke sende melding");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSubmitReport = async () => {
    if (!reportForm.reason) {
      toast.error("Vennligst velg en grunn");
      return;
    }

    try {
      setSubmittingReport(true);
      await axios.post(`${API}/reports`, {
        listing_id: id,
        reason: reportForm.reason,
        description: reportForm.description,
      }, {
        headers: isAuthenticated() ? getAuthHeaders() : {}
      });
      toast.success("Takk for din tilbakemelding!");
      setShowReportDialog(false);
      setReportForm({ reason: "", description: "" });
    } catch (error) {
      console.error("Error submitting report:", error);
      toast.error("Kunne ikke sende rapport");
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleMarkAsSold = async () => {
    try {
      await axios.put(`${API}/listings/${id}`, { status: "sold" });
      toast.success("Annonse markert som solgt!");
      fetchListing();
    } catch (error) {
      console.error("Error marking as sold:", error);
      toast.error("Kunne ikke oppdatere status");
    }
  };

  const handleMarkAsActive = async () => {
    try {
      await axios.put(`${API}/listings/${id}`, { status: "active" });
      toast.success("Annonse markert som aktiv!");
      fetchListing();
    } catch (error) {
      console.error("Error marking as active:", error);
      toast.error("Kunne ikke oppdatere status");
    }
  };

  const handleDeleteListing = async () => {
    try {
      await axios.delete(`${API}/listings/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Annonse slettet!");
      navigate("/");
    } catch (error) {
      console.error("Error deleting listing:", error);
      if (error.response?.status === 403) {
        toast.error("Du har ikke tillatelse til å slette denne annonsen");
      } else {
        toast.error("Kunne ikke slette annonse");
      }
    }
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success("Lenke kopiert til utklippstavlen!");
  };

  const checkIfFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem("favorites") || "[]");
    setIsFavorite(favorites.includes(id));
  };

  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem("favorites") || "[]");
    if (isFavorite) {
      const updated = favorites.filter((favId) => favId !== id);
      localStorage.setItem("favorites", JSON.stringify(updated));
      setIsFavorite(false);
      toast.success("Fjernet fra favoritter");
    } else {
      favorites.push(id);
      localStorage.setItem("favorites", JSON.stringify(favorites));
      setIsFavorite(true);
      toast.success("Lagt til i favoritter");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center">
        <p className="text-gray-500" data-testid="loading-state">Laster...</p>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Annonse ikke funnet</p>
          <Button onClick={() => navigate("/")} className="bg-emerald-600 hover:bg-emerald-700">
            Tilbake til forsiden
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 pb-24">
      {/* Navigation */}
      <Navbar showBackButton={true} showWedgeGuide={false} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Images */}
            <Card className="bg-white shadow-lg">
              <CardContent className="p-0">
                <div className="aspect-video bg-gray-200 overflow-hidden rounded-t-lg">
                  {listing.images && listing.images.length > 0 ? (
                    <>
                      <img
                        src={listing.images[currentImage]}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                        data-testid="listing-main-image"
                      />
                      {listing.images.length > 1 && (
                        <div className="flex gap-2 p-4 justify-center bg-white">
                          {listing.images.map((img, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentImage(idx)}
                              className={`w-16 h-16 rounded border-2 overflow-hidden ${
                                idx === currentImage ? "border-emerald-600" : "border-gray-300"
                              }`}
                              data-testid={`image-thumbnail-${idx}`}
                            >
                              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <span className="text-6xl">⛳</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Listing Details */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <CardTitle className="text-3xl font-bold text-gray-900" data-testid="listing-title">
                        {listing.title}
                      </CardTitle>
                      {listing.status === "sold" && (
                        <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-semibold rounded-full" data-testid="sold-badge">
                          SOLGT
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-gray-600">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span data-testid="listing-location">{listing.location}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-emerald-600" data-testid="listing-price">
                      {listing.price.toLocaleString('nb-NO')} kr
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-2 flex-wrap">
                    <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded font-medium" data-testid="listing-category">
                      {listing.category}
                    </span>
                    <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-sm rounded font-medium" data-testid="listing-condition">
                      {listing.condition}
                    </span>
                  </div>
                  <Separator />
                  
                  {/* Specifications */}
                  {(listing.brand || listing.flex || listing.hand || listing.shaft_material || listing.length || listing.lie) && (
                    <>
                      <div>
                        <h3 className="text-lg font-semibold mb-3 text-gray-900">Spesifikasjoner</h3>
                        <div className="grid grid-cols-2 gap-3">
                          {listing.brand && (
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-xs text-gray-500 mb-1">Merke</p>
                              <p className="font-medium text-gray-900 capitalize">{listing.brand}</p>
                            </div>
                          )}
                          {listing.flex && (
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-xs text-gray-500 mb-1">Stivhet/Flex</p>
                              <p className="font-medium text-gray-900 capitalize">{listing.flex}</p>
                            </div>
                          )}
                          {listing.hand && (
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-xs text-gray-500 mb-1">Hånd</p>
                              <p className="font-medium text-gray-900 capitalize">{listing.hand === 'right' ? 'Høyre' : 'Venstre'}</p>
                            </div>
                          )}
                          {listing.length && (
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-xs text-gray-500 mb-1">Lengde</p>
                              <p className="font-medium text-gray-900 capitalize">{listing.length}</p>
                            </div>
                          )}
                          {listing.shaft_material && (
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-xs text-gray-500 mb-1">Shaft-materiale</p>
                              <p className="font-medium text-gray-900 capitalize">{listing.shaft_material}</p>
                            </div>
                          )}
                          {listing.lie && (
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-xs text-gray-500 mb-1">Lie vinkel</p>
                              <p className="font-medium text-gray-900">
                                {(() => {
                                  const lie = parseFloat(listing.lie);
                                  if (lie === 0) return "0° (Standard)";
                                  if (lie > 0) return `${lie}° Upright`;
                                  return `${Math.abs(lie)}° Flat`;
                                })()}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <Separator />
                    </>
                  )}
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-900">Beskrivelse</h3>
                    <p className="text-gray-700 whitespace-pre-wrap" data-testid="listing-description">
                      {listing.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Messages Section */}
            {messages.length > 0 && (
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-gray-900">Meldinger</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4" data-testid="messages-section">
                    {messages.map((msg) => (
                      <div key={msg.id} className="border-l-4 border-emerald-600 pl-4 py-2" data-testid={`message-${msg.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900">{msg.sender_name}</span>
                          <span className="text-sm text-gray-500">{msg.sender_email}</span>
                        </div>
                        <p className="text-gray-700">{msg.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(msg.created_at).toLocaleString('nb-NO')}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Favorite & Share */}
            <Card className="bg-white shadow-lg">
              <CardContent className="pt-6 space-y-3">
                <Button
                  onClick={toggleFavorite}
                  variant={isFavorite ? "default" : "outline"}
                  className={`w-full ${isFavorite ? "bg-red-500 hover:bg-red-600" : ""}`}
                  data-testid="favorite-button"
                >
                  <Heart className={`w-4 h-4 mr-2 ${isFavorite ? "fill-current" : ""}`} />
                  {isFavorite ? "Fjern fra favoritter" : "Legg til favoritter"}
                </Button>
                
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full"
                      data-testid="share-listing-button"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Del denne annonsen
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Del annonse</DialogTitle>
                      <DialogDescription>
                        Del denne annonsen med andre ved å kopiere lenken
                      </DialogDescription>
                    </DialogHeader>
                    <div className="flex gap-2">
                      <Input
                        value={window.location.href}
                        readOnly
                        className="flex-1"
                        data-testid="share-url-input"
                      />
                      <Button
                        onClick={handleCopyLink}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="copy-link-button"
                      >
                        Kopier
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Report Button */}
                <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      className="w-full text-gray-500 hover:text-red-600"
                      data-testid="report-listing-button"
                    >
                      <Flag className="w-4 h-4 mr-2" />
                      Rapporter annonse
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Rapporter annonse</DialogTitle>
                      <DialogDescription>
                        Fortell oss hvorfor du vil rapportere denne annonsen
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Grunn</Label>
                        <Select
                          value={reportForm.reason}
                          onValueChange={(value) => setReportForm({ ...reportForm, reason: value })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Velg en grunn" />
                          </SelectTrigger>
                          <SelectContent>
                            {reportReasons.map((reason) => (
                              <SelectItem key={reason.value} value={reason.value}>
                                {reason.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Beskrivelse (valgfritt)</Label>
                        <Textarea
                          value={reportForm.description}
                          onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                          placeholder="Gi gjerne mer informasjon..."
                          className="mt-1"
                          rows={3}
                        />
                      </div>
                      <Button
                        onClick={handleSubmitReport}
                        disabled={submittingReport || !reportForm.reason}
                        className="w-full bg-red-600 hover:bg-red-700"
                      >
                        {submittingReport ? "Sender..." : "Send rapport"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            {/* Seller Actions */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-gray-900">Administrer annonse</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {listing.status === "active" ? (
                  <Button
                    onClick={handleMarkAsSold}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="mark-as-sold-button"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Marker som solgt
                  </Button>
                ) : (
                  <Button
                    onClick={handleMarkAsActive}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="mark-as-active-button"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Marker som aktiv
                  </Button>
                )}
                {canEdit && (
                  <Button
                    onClick={() => navigate(`/edit/${id}`)}
                    variant="outline"
                    className="w-full"
                    data-testid="edit-listing-button"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Rediger annonse
                  </Button>
                )}
                {canEdit && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full border-red-300 text-red-600 hover:bg-red-50"
                        data-testid="delete-listing-button"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Slett annonse
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Er du sikker?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Denne handlingen kan ikke angres. Annonsen vil bli permanent slettet.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteListing}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid="confirm-delete-button"
                        >
                          Slett
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </CardContent>
            </Card>

            {/* Seller Info */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-gray-900">Selger</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="font-semibold" data-testid="seller-name">{listing.seller_name}</span>
                </div>
                
                {/* Seller Rating */}
                {listing.creator_id && (
                  <SellerRatingSummary sellerId={listing.creator_id} />
                )}
                
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${listing.seller_email}`} className="hover:text-emerald-600" data-testid="seller-email">
                    {listing.seller_email}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${listing.seller_phone}`} className="hover:text-emerald-600" data-testid="seller-phone">
                    {listing.seller_phone}
                  </a>
                </div>
                
                {/* Review Button */}
                {listing.creator_id && (
                  <div className="pt-2">
                    <ReviewDialog
                      sellerId={listing.creator_id}
                      sellerName={listing.seller_name}
                      listingId={listing.id}
                      listingTitle={listing.title}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Seller Reviews */}
            {listing.creator_id && (
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-gray-900">Vurderinger av selger</CardTitle>
                </CardHeader>
                <CardContent>
                  <ReviewList sellerId={listing.creator_id} />
                </CardContent>
              </Card>
            )}

            {/* Contact Form */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-gray-900">Send melding</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSendMessage} className="space-y-4">
                  <div>
                    <Label htmlFor="sender_name">Navn</Label>
                    <Input
                      id="sender_name"
                      value={messageForm.sender_name}
                      onChange={(e) => setMessageForm({ ...messageForm, sender_name: e.target.value })}
                      placeholder="Ditt navn"
                      className="mt-1"
                      data-testid="message-sender-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sender_email">E-post</Label>
                    <Input
                      id="sender_email"
                      type="email"
                      value={messageForm.sender_email}
                      onChange={(e) => setMessageForm({ ...messageForm, sender_email: e.target.value })}
                      placeholder="din@email.no"
                      className="mt-1"
                      data-testid="message-sender-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message">Melding</Label>
                    <Textarea
                      id="message"
                      value={messageForm.message}
                      onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                      placeholder="Skriv din melding..."
                      rows={4}
                      className="mt-1"
                      data-testid="message-content-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={sendingMessage}
                    data-testid="send-message-button"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingMessage ? "Sender..." : "Send melding"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Similar Listings */}
      {similarListings.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Lignende annonser</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="similar-listings">
            {similarListings.map((item) => (
              <Card
                key={item.id}
                className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white border border-gray-200"
                onClick={() => navigate(`/listing/${item.id}`)}
                data-testid={`similar-listing-${item.id}`}
              >
                <CardContent className="p-0">
                  <div className="aspect-video bg-gray-200 overflow-hidden rounded-t-lg">
                    {item.images && item.images.length > 0 ? (
                      <img
                        src={item.images[0]}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <span className="text-4xl">⛳</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-lg text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-emerald-600">
                        {item.price.toLocaleString('nb-NO')} kr
                      </span>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-4 h-4 mr-1" />
                        {item.location}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingDetailPage;